import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { DynamicGameSettingsForm } from '../DynamicGameSettingsForm';
import type { GameFieldConfig, ServerPackage, CreatePackageRequest, UpdatePackageRequest } from '../../types/package';
import { SUPPORTED_GAMES } from '../../types/package';
import { PackageService } from '../../services/packageService';

interface PackageModalProps {
  isOpen: boolean;
  editingPackage: ServerPackage | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData {
  name: string;
  displayName: string;
  description: string;
  game: string;
  price: number;
  ram: number;
  cpu: number;
  storage: number;
  maxPlayers: number;
  isFree: boolean;
  gameSettings: { [key: string]: any };
  autoShutdown: { enabled: boolean; idleMinutes: number };
}

const emptyForm: FormData = {
  name: '',
  displayName: '',
  description: '',
  game: '',
  price: 0,
  ram: 1,
  cpu: 1,
  storage: 10,
  maxPlayers: 10,
  isFree: false,
  gameSettings: {},
  autoShutdown: { enabled: false, idleMinutes: 30 }
};

export const PackageModal: React.FC<PackageModalProps> = ({ isOpen, editingPackage, onClose, onSaved }) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [fieldConfigs, setFieldConfigs] = useState<GameFieldConfig[]>([]);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editingPackage) {
      setFormData({
        name: editingPackage.name,
        displayName: editingPackage.displayName,
        description: editingPackage.description,
        game: editingPackage.game,
        price: editingPackage.price,
        ram: editingPackage.ram,
        cpu: editingPackage.cpu ?? 1,
        storage: editingPackage.storage ?? 10,
        maxPlayers: editingPackage.maxPlayers,
        isFree: editingPackage.isFree,
        gameSettings: Object.fromEntries(
          Object.entries(editingPackage.gameSettings || {}).filter(([name]) => name !== 'memory')
        ),
        autoShutdown: editingPackage.autoShutdown || { enabled: false, idleMinutes: 30 }
      });
    } else {
      setFormData(emptyForm);
    }
  }, [isOpen, editingPackage]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!formData.game) {
        setFieldConfigs([]);
        return;
      }
      try {
        const cfgs = await PackageService.getPackageDefaultFieldConfigs(formData.game);
        if (cancelled) return;
        setFieldConfigs(cfgs);
        if (editingPackage && formData.game === editingPackage.game) {
          const originalSettings = Object.fromEntries(
            Object.entries(editingPackage.gameSettings || {}).filter(([name]) => name !== 'memory')
          );
          setFormData(prev => ({ ...prev, gameSettings: originalSettings }));
        } else {
          const defaults: Record<string, any> = {};
          cfgs.forEach(c => {
            if (c.defaultValue !== undefined) defaults[c.name] = c.defaultValue;
          });
          setFormData(prev => ({ ...prev, gameSettings: defaults }));
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load configs', e);
        setFieldConfigs([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [formData.game, editingPackage]);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleGameSettingChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, gameSettings: { ...prev.gameSettings, [name]: value } }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Required';
    if (!formData.displayName.trim()) errors.displayName = 'Required';
    if (!formData.description.trim()) errors.description = 'Required';
    if (!formData.game.trim()) errors.game = 'Required';
    if (formData.ram <= 0) errors.ram = 'Must be > 0';
    if (formData.cpu <= 0) errors.cpu = 'Must be > 0';
    if (formData.storage <= 0) errors.storage = 'Must be > 0';
    if (formData.maxPlayers <= 0) errors.maxPlayers = 'Must be > 0';
    if (
      formData.autoShutdown.enabled
      && (!Number.isInteger(formData.autoShutdown.idleMinutes)
        || formData.autoShutdown.idleMinutes < 5
        || formData.autoShutdown.idleMinutes > 10_080)
    ) {
      errors.autoShutdown = 'Use a whole number between 5 and 10080 minutes';
    }
    fieldConfigs.forEach(cfg => {
      if (cfg.required && (formData.gameSettings[cfg.name] === undefined || formData.gameSettings[cfg.name] === '')) {
        errors[`gameSettings.${cfg.name}`] = `${cfg.label} required`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreatePackageRequest | UpdatePackageRequest = {
      name: formData.name,
      displayName: formData.displayName,
      description: formData.description,
      game: formData.game,
      price: formData.price,
      ram: formData.ram,
      cpu: formData.cpu,
      storage: formData.storage,
      maxPlayers: formData.maxPlayers,
      isFree: formData.isFree,
      gameSettings: formData.gameSettings,
      autoShutdown: formData.autoShutdown
    };

    try {
      setSubmitting(true);
      if (editingPackage?._id) {
        await PackageService.updatePackage(editingPackage._id, payload);
      } else {
        await PackageService.createPackage(payload as CreatePackageRequest);
      }
      onSaved();
    } catch (err: any) {
      setFormErrors(prev => ({ ...prev, submit: err?.message || 'Failed to save' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-dark-600">
          <h2 className="text-xl font-bold text-gray-100">
            {editingPackage ? 'Edit Package' : 'New Package'}
          </h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="input"
                placeholder="minecraft-basic"
              />
              {formErrors.name && <p className="text-xs text-red-400 mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                className="input"
                placeholder="Minecraft Basic"
              />
              {formErrors.displayName && <p className="text-xs text-red-400 mt-1">{formErrors.displayName}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-gray-300 mb-1 block">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={2}
                className="input resize-none"
                placeholder="Package description..."
              />
              {formErrors.description && <p className="text-xs text-red-400 mt-1">{formErrors.description}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Game</label>
              <input
                list="game-suggestions"
                value={formData.game}
                onChange={(e) => handleChange('game', e.target.value)}
                className="input"
                placeholder="minecraft"
              />
              <datalist id="game-suggestions">
                {SUPPORTED_GAMES.map(g => (<option key={g} value={g} />))}
              </datalist>
              {formErrors.game && <p className="text-xs text-red-400 mt-1">{formErrors.game}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Price (RON)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={formData.price}
                onChange={(e) => handleChange('price', Number(e.target.value))}
                className="input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">RAM (GB)</label>
              <input
                type="number"
                min={0.5}
                step={0.5}
                value={formData.ram}
                onChange={(e) => handleChange('ram', Number(e.target.value))}
                className="input"
              />
              {formErrors.ram && <p className="text-xs text-red-400 mt-1">{formErrors.ram}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">vCPU</label>
              <input
                type="number"
                min={0.5}
                step={0.5}
                value={formData.cpu}
                onChange={(e) => handleChange('cpu', Number(e.target.value))}
                className="input"
              />
              {formErrors.cpu && <p className="text-xs text-red-400 mt-1">{formErrors.cpu}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Storage (GB)</label>
              <input
                type="number"
                min={1}
                step={1}
                value={formData.storage}
                onChange={(e) => handleChange('storage', Number(e.target.value))}
                className="input"
              />
              {formErrors.storage && <p className="text-xs text-red-400 mt-1">{formErrors.storage}</p>}
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Max Players</label>
              <input
                type="number"
                min={1}
                value={formData.maxPlayers}
                onChange={(e) => handleChange('maxPlayers', Number(e.target.value))}
                className="input"
              />
              {formErrors.maxPlayers && <p className="text-xs text-red-400 mt-1">{formErrors.maxPlayers}</p>}
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isFree}
                onChange={(e) => handleChange('isFree', e.target.checked)}
                className="checkbox"
              />
              <span className="text-sm text-gray-300">Free package</span>
            </label>
          </div>

          {/* Auto Shutdown */}
          <div className="p-4 bg-dark-700 rounded-lg">
            <h4 className="text-sm font-medium text-gray-200 mb-3">Auto Shutdown</h4>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.autoShutdown.enabled}
                  onChange={(e) => handleChange('autoShutdown', { ...formData.autoShutdown, enabled: e.target.checked })}
                  className="checkbox"
                />
                <span className="text-sm text-gray-300">Enable when idle</span>
              </label>
              {formData.autoShutdown.enabled && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={5}
                    max={10080}
                    step={1}
                    value={formData.autoShutdown.idleMinutes}
                    onChange={(e) => handleChange('autoShutdown', { ...formData.autoShutdown, idleMinutes: Number(e.target.value) })}
                    className="input w-20"
                  />
                  <span className="text-sm text-gray-400">minutes</span>
                </div>
              )}
            </div>
            {formErrors.autoShutdown && <p className="text-xs text-red-400 mt-2">{formErrors.autoShutdown}</p>}
          </div>

          {/* Game Settings */}
          {formData.game && fieldConfigs.length > 0 && (
            <div className="border-t border-dark-600 pt-6">
              <DynamicGameSettingsForm
                fieldConfigs={fieldConfigs}
                values={formData.gameSettings}
                onChange={handleGameSettingChange}
                errors={formErrors}
              />
            </div>
          )}

          {formErrors.submit && (
            <p className="text-sm text-red-400">{formErrors.submit}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-dark-600">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <><div className="spinner w-4 h-4" /> Saving...</>
              ) : (
                editingPackage ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
